// React
import { Fragment, useContext } from "react"

// Components
import { ActionButton, Button, Loading, Modal } from "components"
import { FormSection } from "../../../../FormSection"

// Configs
import { theme } from "configs"

// Form
import * as yup from "yup"
import { FormProvider, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"

// Third-Party Libraries
import toast from "react-hot-toast"
import { random } from "uniqolor"

// Types
import type { DataList } from "../../../../../types"

// Utils
import { useApi, useToggle } from "utils"
import { Render } from "../../../../../utils"
import { useDetail } from "../utils"
import { IsActive } from "./IsActive"

export function Update(props: { id: DataList, refetch: () => void }) {
  const { isActive, toggle } = useToggle(false)

  return (
    <Fragment>
      <ActionButton.Update className="join-item" onClick={toggle} />
      {isActive && <ModalSection id={props.id} toggle={toggle} refetch={props.refetch}/>}
    </Fragment>
  )
}

function ModalSection(props: { id: DataList; toggle: () => void; refetch: () => void }) {
  const { data, dataError, isLoading } = useDetail()

  return (
    <Modal isOpen title="EDIT LOCATION" size="xl" closeModal={props.toggle}>
      {isLoading || dataError ? (
        <Loading errorText={dataError} loading={isLoading} />
      ) : (
        <DataForm data={data!} id={props.id} toggle={props.toggle} refetch={props.refetch} />
      )}
    </Modal>
  )
}

function DataForm(props: { data: DataList; id: DataList; toggle: () => void; refetch: () => void }) {
  const api = useApi()
  const { toggle } = useContext(Render)

  type CreateData = Omit<DataList, "location_id" | "country_name" | "province_name" | "regency_name" | "subdistrict_name" | "village_name"> 

  const defaultValues: CreateData= {
    code: props?.id.code,
    location_name: props?.id.location_name ?? "",
    address: props?.id.address ?? "",
    description: props?.id.description ?? "",
    country_id: Number(props?.id?.country_id) ?? "",
    province_id: Number(props?.id?.province_id) ?? "",
    regency_id: Number(props?.id?.regency_id) ?? "",
    subdistrict_id: Number(props?.id?.subdistrict_id) ?? "",
    village_id: Number(props?.id?.village_id) ?? "",
    color: props?.id?.color ?? random().color,
  }
  const validationSchema = yup.object().shape({ 
    code: yup.string().label("CODE").required().min(2).max(3),
    location_name: yup.string().label("Location name").required().max(100, "Must be 100 characters or less"),
    address: yup.string().label("Address").optional().max(100, "Must be 100 characters or less"),
    description: yup.string().label("Description").optional()
  })
  const onSubmit = ( value: CreateData ) => {
    return new Promise<void>((resolve) => {
      if (value.location_name === theme.location_name) {
        toast.error("Location name has been set as default location")
        return resolve()
      }

      toast.promise(
        api.put(`/location/edit?location_id=${props.id.location_id}`, 
        { 
          code: value.code, 
          location_name: value.location_name, 
          address: value.address,
          country_id: value.country_id,
          province_id: value.province_id,
          regency_id: value.regency_id,
          subdistrict_id: value.subdistrict_id,
          village_id: value.village_id,
          description: value.description, 
          color: value.color, 
        }),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.message
        }
      ).then(() => {
        props.toggle()
        props?.refetch && props.refetch()
        toggle()
      }).catch(() => {}).finally(resolve)
    })
  }
  const methods = useForm<CreateData>({
    defaultValues: defaultValues,
    resolver: yupResolver(validationSchema)
  })

  return (
    <FormProvider {...methods}>
      {/* @ts-ignore */}
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FormSection isUpdate />

        <Modal.Footer className="flex justify-between gap-1">
          <IsActive id={props.id} refetch={props.refetch} />
          <Button type="submit" color="primary" loading={methods.formState.isSubmitting ? "true" : undefined}>SAVE</Button>
        </Modal.Footer>
      </form>
    </FormProvider>
  )
}